import { useMemo } from 'react';
import { useAuth } from '../contexts/Auth/authContext';

/**
 * What this session may do, from its roles and its scope. Mirrors the
 * require_* gates of the API (docs/droits-acces.md there has the matrix);
 * the API decides, this only hides what it would refuse.
 *
 * | ANEP        | clients   | types, templates | firmware | accounts     | audit | portability | administration | gateway: add, General | Settings, Programming | Raw, V2/conf |
 * |-------------|-----------|------------------|----------|--------------|-------|-------------|----------------|-----------------------|-----------------------|--------------|
 * | SuperAdmin  | yes       | yes              | yes      | read + write | yes   | yes         | yes            | yes                   | yes                   | yes          |
 * | Admin       | read only | yes              | yes      | read only    | yes   | yes         | no             | yes                   | yes                   | yes          |
 * | Operator    | read only | read only        | yes      | no           | yes   | no          | no             | no                    | no                    | yes          |
 *
 * | Client      | own gateways: add, General | own templates | Settings, Programming | Raw, V2/conf | History | own accounts | portability |
 * |-------------|----------------------------|---------------|-----------------------|--------------|---------|--------------|-------------|
 * | ClientAdmin | yes                        | yes           | no                    | no           | yes     | read + write | yes         |
 * | Viewer      | read only                  | read only     | no                    | no           | yes     | no           | no          |
 */

export const useClientAccess = () => {
  const { user, serverScope } = useAuth();

  return useMemo(() => {
    const roles = user?.roles ?? [];
    const isSuperAdmin = roles.includes('SuperAdmin');
    const isAdmin = roles.includes('Admin');
    const isOperator = roles.includes('Operator');
    const isClientAdmin = roles.includes('ClientAdmin');
    const isViewer = roles.includes('Viewer');

    // The realm always adds default-roles-*, offline_access and
    // uma_authorization, so `roles.length` is never a usable test.
    const hasApplicationRole = isSuperAdmin || isAdmin || isOperator || isClientAdmin || isViewer;

    const isAnepStaff = isSuperAdmin || isAdmin || isOperator || (user?.isAnepStaff ?? false);
    // à voir 
    // isanepmanager est un membre du personnel ANEP qui a le rôle de SuperAdmin ou Admin, donc il peut gérer les clients et voir le journal d'audit.
    const isAnepManager = isAnepStaff && (isSuperAdmin || isAdmin);
    const isAnepOperator = isAnepStaff && isOperator;

    const isClientStaff = !isAnepStaff && isClientAdmin;

    // What the server says this session may reach, once it has answered;
    // the token's own reading until then.
    const allowedClientIds = serverScope ? serverScope.clientIds : (user?.clientIds ?? []);
    const scopeResolved = serverScope != null;
    const hasSingleClient = allowedClientIds.length === 1;

    const hasOnlyViewerRole = isViewer && !isSuperAdmin && !isAdmin && !isOperator && !isClientAdmin;

    // Accounts: read for SuperAdmin, Admin and a client's own admin; write
    // for SuperAdmin and ClientAdmin only. Mirrors USER_MANAGEMENT_ROLES /
    // USER_WRITE_ROLES on the API.
    const canManageUsers = isSuperAdmin || isAdmin || isClientAdmin;
    const canWriteUsers = isSuperAdmin || isClientAdmin;

    // Firmware is ANEP-side and open to every ANEP role for writing.
    // Mirrors FLEET_WRITE_ROLES.
    const canWriteCatalog = isAnepManager || isAnepOperator;

    // Hardware types and model references: the managers write, an Operator
    // reads (the page itself is ANEP-only, canAccessCatalog). Mirrors
    // require_admin on /v2/hardware-types and /v2/model-refs.
    const canEditDeviceTypes = isAnepManager;

    // Configuration templates are for everyone to read; the managers write
    // any template, a ClientAdmin their own client's and agencies', an
    // Operator and a Viewer read. Bulk assignment follows the same rule.
    // Mirrors TEMPLATE_WRITE_ROLES.
    const canWriteTemplates = isAnepManager || isClientStaff;
    const canAssignTemplates = canWriteTemplates;

    // One gateway. Adding one and its General tab (identity, client, type,
    // template): the ANEP managers and a client's own admin — an Operator
    // reads the fleet and runs the catalogue, a client's gateways are not
    // theirs to change. Its Settings (the configuration override) and its
    // programming: the managers only. Its raw row and what it receives on
    // the wire (V2/conf): the ANEP side. Its history: everyone who opens it.
    const canManageDevices = isAnepManager || (isClientAdmin && allowedClientIds.length > 0);
    const canEditDeviceSettings = isAnepManager;
    const canProgramDevices = isAnepManager;
    const canViewDeviceInternals = isAnepStaff;

    return {
      isSuperAdmin,
      isAdmin,
      isOperator,
      isClientAdmin,
      isViewerOnly: hasOnlyViewerRole,
      isAnepStaff,
      isAnepManager,
      isClientStaff,
      hasApplicationRole,

      // Clients and agencies: SuperAdmin alone adds or changes them, the
      // other ANEP roles read the page. Mirrors require_superadmin on
      // /v2/client.
      canManageClients: isAnepStaff && isSuperAdmin,
      shouldShowClientUI: isAnepStaff,

      // The audit log page is for the ANEP group; a client's accounts read
      // one gateway's history from its dialog. Mirrors ANEP_ROLES on
      // /v2/audit-log.
      canViewAuditLog: isAnepStaff,

      // Portability, one client taking over another's gateways: the ANEP
      // managers and a client's own admin ask and answer, an Operator and a
      // Viewer have no part in it. Mirrors require_device_write on
      // /v2/device-transfers.
      canUsePortability: isAnepManager || isClientAdmin,

      // Administration, the interface settings: SuperAdmin alone. Mirrors
      // require_superadmin on /v2/ui-settings.
      canAdministrate: isAnepStaff && isSuperAdmin,

      canAccessCatalog: isAnepStaff,
      canAccessOtaUpdates: isAnepStaff,
      canManageFirmware: canWriteCatalog,
      canEditDeviceTypes,

      canAccessTemplates: hasApplicationRole,
      canWriteTemplates,
      canAssignTemplates,

      canManageUsers,
      canWriteUsers,

      canManageDevices,
      canEditDeviceSettings,
      canProgramDevices,
      canViewDeviceInternals,

      // Shut out: no application role at all on this environment, or a
      // client-side session whose scope came back empty — never granted,
      // revoked, or the whole client suspended. A `null` scope is "not
      // answered yet" and must not lock anyone out.
      isLockedOut:
        !hasApplicationRole ||
        (scopeResolved && !isAnepStaff && allowedClientIds.length === 0),

      allowedClientIds,
      hasFullAccess: isAnepStaff,
      hasSingleClient,
      hasAnyClientAccess: isAnepStaff || allowedClientIds.length > 0,

      canAccessClient: (clientId: string): boolean => {
        if (isAnepStaff) return true;
        return allowedClientIds.includes(clientId);
      },

      filterByClientAccess: <T extends { client_id?: string | null }>(items: T[]): T[] => {
        if (isAnepStaff) return items;
        return items.filter(item => {
          if (!item.client_id) return true;
          return allowedClientIds.includes(item.client_id);
        });
      },
    };
  }, [user?.roles, user?.clientIds, user?.isAnepStaff, serverScope]);
};

export default useClientAccess;
